import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import exerciseLibraryData from '../data/exercise-library-expanded.json';
import measurementTypesData from '../data/measurement-types.json';

class ExerciseLibraryService {
  constructor() {
    this.exercisesCache = null;
    this.customExercisesCache = null;
  }

  // Get all default exercises from JSON
  getDefaultExercises() {
    if (this.exercisesCache) {
      return this.exercisesCache;
    }

    const exercises = [];
    const library = exerciseLibraryData.exerciseLibrary;

    // Process resistance training
    if (library.resistance) {
      Object.keys(library.resistance).forEach(category => {
        const categoryData = library.resistance[category];
        if (categoryData.exercises) {
          exercises.push(...categoryData.exercises.map(ex => ({
            ...ex,
            category: 'resistance',
            subcategory: category,
            isDefault: true
          })));
        }
      });
    }

    // Process anaerobic
    if (library.anaerobic) {
      Object.keys(library.anaerobic).forEach(category => {
        const categoryData = library.anaerobic[category];
        if (categoryData.exercises) {
          exercises.push(...categoryData.exercises.map(ex => ({
            ...ex,
            category: 'anaerobic',
            subcategory: category,
            isDefault: true
          })));
        }
      });
    }

    // Process aerobic
    if (library.aerobic) {
      Object.keys(library.aerobic).forEach(category => {
        const categoryData = library.aerobic[category];
        if (categoryData.exercises) {
          exercises.push(...categoryData.exercises.map(ex => ({
            ...ex,
            category: 'aerobic',
            subcategory: category,
            isDefault: true
          })));
        }
      });
    }

    // Process olympic_lifts (top-level category)
    if (library.olympic_lifts && library.olympic_lifts.exercises) {
      exercises.push(...library.olympic_lifts.exercises.map(ex => ({
        ...ex,
        category: 'olympic_lifts',
        subcategory: 'olympic_lifts',
        isDefault: true
      })));
    }

    // Process hiit (top-level category)
    if (library.hiit && library.hiit.exercises) {
      exercises.push(...library.hiit.exercises.map(ex => ({
        ...ex,
        category: 'hiit',
        subcategory: 'hiit',
        isDefault: true
      })));
    }

    // Process flexibility_mobility (top-level category)
    if (library.flexibility_mobility && library.flexibility_mobility.exercises) {
      exercises.push(...library.flexibility_mobility.exercises.map(ex => ({
        ...ex,
        category: 'flexibility_mobility',
        subcategory: 'flexibility_mobility',
        isDefault: true
      })));
    }

    this.exercisesCache = exercises;
    return exercises;
  }

  // Get all custom exercises (global - accessible to all coaches)
  async getCustomExercises() {
    try {
      const exercisesRef = collection(db, 'customExercises');
      // Fetch ALL custom exercises, not just for specific coach
      const querySnapshot = await getDocs(exercisesRef);

      const customExercises = [];
      querySnapshot.forEach((doc) => {
        customExercises.push({
          id: doc.id,
          ...doc.data(),
          isDefault: false,
          isCustom: true
        });
      });

      this.customExercisesCache = customExercises;
      return customExercises;
    } catch (error) {
      console.error('[ExerciseLibrary] Error fetching custom exercises:', error);
      return [];
    }
  }

  // Get all exercises (default + custom)
  async getAllExercises() {
    const defaultExercises = this.getDefaultExercises();
    const customExercises = await this.getCustomExercises();
    return [...defaultExercises, ...customExercises];
  }

  // Create custom exercise (global - accessible to all coaches)
  async createCustomExercise(coachId, exerciseData, creatorName = 'Unknown Coach') {
    try {
      const newExercise = {
        ...exerciseData,
        createdBy: coachId,
        creatorName: creatorName,
        isCustom: true,
        isGlobal: true, // Mark as globally accessible
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'customExercises'), newExercise);

      // Update cache (global cache, not per-coach)
      const exerciseWithId = {
        id: docRef.id,
        ...newExercise,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      if (this.customExercisesCache) {
        this.customExercisesCache.push(exerciseWithId);
      }

      return exerciseWithId;
    } catch (error) {
      console.error('[ExerciseLibrary] Error creating custom exercise:', error);
      throw error;
    }
  }

  // Update custom exercise
  async updateCustomExercise(exerciseId, updates) {
    try {
      const exerciseRef = doc(db, 'customExercises', exerciseId);
      await updateDoc(exerciseRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });

      // Update cache
      if (this.customExercisesCache) {
        const index = this.customExercisesCache.findIndex(ex => ex.id === exerciseId);
        if (index !== -1) {
          this.customExercisesCache[index] = {
            ...this.customExercisesCache[index],
            ...updates,
            updatedAt: new Date()
          };
        }
      }

      return true;
    } catch (error) {
      console.error('[ExerciseLibrary] Error updating custom exercise:', error);
      throw error;
    }
  }

  // Delete custom exercise
  async deleteCustomExercise(exerciseId) {
    try {
      await deleteDoc(doc(db, 'customExercises', exerciseId));

      // Update cache
      if (this.customExercisesCache) {
        this.customExercisesCache = this.customExercisesCache.filter(ex => ex.id !== exerciseId);
      }

      return true;
    } catch (error) {
      console.error('[ExerciseLibrary] Error deleting custom exercise:', error);
      throw error;
    }
  }

  // Search exercises
  searchExercises(exercises, searchTerm, filters = {}) {
    let filtered = exercises;

    // Text search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(ex =>
        ex.name.toLowerCase().includes(term) ||
        ex.primaryMuscles?.some(m => m.toLowerCase().includes(term)) ||
        ex.equipment?.toLowerCase().includes(term)
      );
    }

    // Category filter
    if (filters.category && filters.category !== 'all') {
      filtered = filtered.filter(ex => ex.category === filters.category);
    }

    // Subcategory filter
    if (filters.subcategory && filters.subcategory !== 'all') {
      filtered = filtered.filter(ex => ex.subcategory === filters.subcategory);
    }

    // Equipment filter
    if (filters.equipment && filters.equipment !== 'all') {
      filtered = filtered.filter(ex => ex.equipment === filters.equipment);
    }

    // Difficulty filter
    if (filters.difficulty && filters.difficulty !== 'all') {
      filtered = filtered.filter(ex => ex.difficulty === filters.difficulty);
    }

    // Muscle group filter
    if (filters.muscleGroup && filters.muscleGroup !== 'all') {
      filtered = filtered.filter(ex =>
        ex.primaryMuscles?.includes(filters.muscleGroup) ||
        ex.secondaryMuscles?.includes(filters.muscleGroup)
      );
    }

    return filtered;
  }

  // Get measurement types
  getMeasurementTypes() {
    return measurementTypesData.measurementTypes;
  }

  // Get measurement fields for specific type
  getMeasurementFields(measurementType, category = 'resistance') {
    const types = this.getMeasurementTypes();
    return types[category]?.[measurementType]?.fields || [];
  }

  // Get categories
  getCategories() {
    return [
      { id: 'all', name: 'All Categories' },
      { id: 'resistance', name: 'Resistance Training' },
      { id: 'anaerobic', name: 'Anaerobic Training' },
      { id: 'olympic_lifts', name: 'Olympic Lifts & Power' },
      { id: 'hiit', name: 'HIIT & Metcon' },
      { id: 'flexibility_mobility', name: 'Flexibility & Mobility' },
      { id: 'aerobic', name: 'Aerobic/Cardio' }
    ];
  }

  // Get subcategories for a category
  getSubcategories(category) {
    const library = exerciseLibraryData.exerciseLibrary;

    if (category === 'all') {
      return [{ id: 'all', name: 'All' }];
    }

    if (!library[category]) {
      return [{ id: 'all', name: 'All' }];
    }

    // Handle flat categories (categories that contain exercises directly, not subcategories)
    const flatCategories = ['olympic_lifts', 'hiit', 'flexibility_mobility'];
    if (flatCategories.includes(category)) {
      if (library[category].exercises) {
        return [
          { id: 'all', name: 'All' },
          { id: category, name: library[category].categoryName || category }
        ];
      }
      return [{ id: 'all', name: 'All' }];
    }

    // Handle nested categories (categories with subcategories)
    const subcategories = Object.keys(library[category])
      .filter(key => key !== 'categoryName' && key !== 'exercises')
      .map(key => ({
        id: key,
        name: library[category][key].categoryName || key
      }));

    return [{ id: 'all', name: 'All' }, ...subcategories];
  }

  // Get equipment types
  getEquipmentTypes() {
    return [
      { id: 'all', name: 'All Equipment' },
      { id: 'barbell', name: 'Barbell' },
      { id: 'dumbbell', name: 'Dumbbell' },
      { id: 'cable_machine', name: 'Cable Machine' },
      { id: 'machine', name: 'Machine' },
      { id: 'bodyweight', name: 'Bodyweight' },
      { id: 'kettlebell', name: 'Kettlebell' },
      { id: 'resistance_band', name: 'Resistance Band' },
      { id: 'suspension', name: 'Suspension Trainer' },
      { id: 'cardio_equipment', name: 'Cardio Equipment' },
      { id: 'other', name: 'Other' }
    ];
  }

  // Get difficulty levels
  getDifficultyLevels() {
    return [
      { id: 'all', name: 'All Levels' },
      { id: 'beginner', name: 'Beginner' },
      { id: 'intermediate', name: 'Intermediate' },
      { id: 'advanced', name: 'Advanced' },
      { id: 'expert', name: 'Expert' }
    ];
  }

  // Clear cache
  clearCache() {
    this.exercisesCache = null;
    this.customExercisesCache = null;
  }
}

export default new ExerciseLibraryService();
